import React, { useState, useEffect, useRef } from "react";
import * as turf from "@turf/turf";
import { toJpeg } from "html-to-image";

import Box from "@mui/material/Box";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";

import MapIcon from "@mui/icons-material/Map";
import HomeIcon from "@mui/icons-material/Home";
import ClearIcon from "@mui/icons-material/Clear";
import LayersIcon from "@mui/icons-material/Layers";
import ModeOutlined from "@mui/icons-material/ModeOutlined";
import PlaceOutlinedIcon from "@mui/icons-material/PlaceOutlined";
import PolylineOutlinedIcon from "@mui/icons-material/PolylineOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import HdrAutoIcon from "@mui/icons-material/HdrAuto";

import Collection from "ol/Collection";
import Draw from "ol/interaction/Draw";
import Modify from "ol/interaction/Modify";
import Snap from "ol/interaction/Snap";
import Translate from "ol/interaction/Translate";
import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import LineString from "ol/geom/LineString";
import Polygon, { circular } from "ol/geom/Polygon";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import Stroke from "ol/style/Stroke";
import Fill from "ol/style/Fill";

import MapFullScreen from "./mapFullScreen";
import MapInfoCard from "./mapInfoCard";
import MapLaancLayersDropdown from "./mapLaancLayersDropdown";
import MapTileDropdown from "./mapTileDropdown";

import { ConvertFeetToMeters, ConvertMetersToFeet, GetColorFromState } from "../util";
import { getGeofenceFeatureFromGeometryAndBuffer, GetCoordinatesForSubmissions, ConvertKmlFileToFeatures, ConvertGeometryToGeoJson } from "../map/mapUtil";
import { convertHexColorToRgbaColor, GetAllFeaturesWithId, JumpToFeature, JumpToFeatures, GetLayer, ConvertCmpVolumesToFeatures } from "../map/mapUtil";
import { ConvertLAANCVolumesToFeatures, GetFeature, GetGeofenceStyle, GetEditVolumeStyle } from "../map/mapUtil";

import { getUpdatedWaypointsFromCoordinatesAndDefaultAltitude, getWaypointFeatureStyleFromFeatureAndSettings } from "../map/mapUtil";
import { getGeofenceCoordinatesFromCoordinatesTypeAndBuffer } from "../map/mapUtil";

import useOpenLayersMap from "../hooks/useOpenLayersMap";

import { useMap } from "../contexts/mapContext";
import { useUserAuth } from "../contexts/authContext";

const DrawType = {
    UPLOAD: {
        name: "Upload",
        icon: <FileUploadOutlinedIcon sx={{ width: "100%" }} />
    },
    AUTOMATIC: {
        name: "Automatic",
        icon: <HdrAutoIcon sx={{ width: "100%" }} />
    },
    POLYGON: {
        name: "Polygon",
        icon: <ModeOutlined sx={{ width: "100%" }} />
    },
    WAYPOINTS: {
        name: "Waypoints",
        icon: <PolylineOutlinedIcon sx={{ width: "100%" }} />
    },
    CIRCLE: {
        name: "Circle",
        icon: <PlaceOutlinedIcon sx={{ width: "100%" }} />
    },
    CLEAR: {
        name: "Clear",
        icon: <ClearIcon sx={{ width: "100%" }} />
    }
};

export const VolumeType = {
    FLIGHT: {
        name: "Create Flight",
        description: "Map used to create flights.",
        layers: {
            operations: true,
            constraints: true,
            alerts: false,
            airspaces: true,
            laanc: true,
            externalFlights: true
        },
        drawTypes: [DrawType.UPLOAD, DrawType.POLYGON, DrawType.WAYPOINTS, DrawType.CIRCLE, DrawType.CLEAR]
    },
    CONSTRAINT: {
        name: "Create Constraint",
        description: "Map used to create constraints.",
        layers: {
            operations: true,
            constraints: true,
            alerts: false,
            airspaces: true,
            laanc: false,
            externalFlights: false
        },
        drawTypes: [DrawType.UPLOAD, DrawType.POLYGON, DrawType.CIRCLE, DrawType.CLEAR]
    },
    ALERT: {
        name: "Create Alert",
        description: "Map used to create alerts.",
        layers: {
            operations: true,
            constraints: false,
            alerts: true,
            airspaces: true,
            laanc: false,
            externalFlights: false
        },
        drawTypes: [DrawType.UPLOAD, DrawType.AUTOMATIC, DrawType.POLYGON, DrawType.CIRCLE, DrawType.CLEAR]
    },
    COA: {
        name: "Create COA Volume",
        description: "Map used to create COA volumes.",
        layers: {
            operations: true,
            constraints: false,
            alerts: false,
            coa: true,
            airspaces: true,
            laanc: false,
            externalFlights: false
        },
        drawTypes: [DrawType.UPLOAD, DrawType.POLYGON, DrawType.CLEAR]
    },
    TEST: {
        name: "Map Draw",
        description: "Used for unit testing.",
        layers: {
            operations: false,
            constraints: false,
            alerts: false,
            airspaces: false,
            laanc: false,
            externalFlights: false
        },
        drawTypes: [DrawType.UPLOAD, DrawType.AUTOMATIC, DrawType.WAYPOINTS, DrawType.POLYGON, DrawType.CIRCLE, DrawType.CLEAR]
    }
};

export function MapDrawComponent(props) {
    const MAP_ID = "map-draw-map";

    const [volumeType, setVolumeType] = useState(null);
    const [drawType, setDrawType] = useState("Polygon");
    const [alertOperation, setAlertOperation] = useState(null);

    const { userMapSettings, setSnackbar } = useUserAuth();
    const { colors } = useMap();

    const [mapLayersOpen, setMapLayersOpen] = useState(false);
    const [mapLayersAnchorEl, setMapLayersAnchorEl] = useState(null);

    const [mapTileDropdownOpen, setMapTileDropdownOpen] = useState(false);
    const [mapTileDropdownAnchorEl, setMapTileDropdownAnchorEl] = useState(null);

    const [selectedLaancFeature, setSelectedLaancFeature] = useState(null);
    const [clearFeature, setClearFeature] = useState(false);

    const [airspaceSubLayers, setAirspaceSublayers] = useState([
        { local_type: "CLASS_B", visible: true, border: "2px solid #0087bf" },
        { local_type: "CLASS_C", visible: true, border: "2px solid #9d6b9e" },
        { local_type: "CLASS_D", visible: true, border: "2px dotted #058eb8" },
        { local_type: "CLASS_E2", visible: true, border: "2px dotted #9e6e9d" }
    ]);

    const mapLocationRef = useRef(null);
    const defaultWaypointAltitudeRef = useRef(200);

    const volumeUploadRef = useRef(null);
    const volumeLayerRef = useRef(null);
    const volumeFeaturesRef = useRef(null); // represents the operational volume that will be submitted
    const volumeAutomaticFeaturesRef = useRef(null); // for automatic buffer features
    const volumeWaypointFeatureRef = useRef(null); // represents the flight path feature
    const volumeGeofenceBufferRef = useRef(100);
    const volumeWaypointsRef = useRef([]); // for the waypoints field in the volume submission

    const drawRef = useRef(null);
    const translateRef = useRef(null);
    const modifyRef = useRef(null);
    const snapRef = useRef(null);

    const map = useOpenLayersMap(MAP_ID, false);

    useEffect(() => {
        if (!map) return;

        const handlePointerMove = ({ originalEvent }) => {
            const coordinates = map.getEventCoordinate(originalEvent);
            if (mapLocationRef.current) {
                mapLocationRef.current.innerHTML = `( ${coordinates[1]}, ${coordinates[0]} )`;
            }
        };
        map.on("pointermove", handlePointerMove);

        return () => {
            if (!map) return;
            map.un("pointermove", handlePointerMove);
        };
    }, [map]);

    useEffect(() => {
        if (isNaN(props.defaultWaypointAltitudeAglFt) === false) {
            defaultWaypointAltitudeRef.current = props.defaultWaypointAltitudeAglFt;
        }
    }, [props.defaultWaypointAltitudeAglFt]);

    useEffect(() => {
        const volumeType = VolumeType[props.type?.toUpperCase()];
        if (volumeType) {
            setVolumeType(volumeType);
        }
    }, [props.type]);

    useEffect(() => {
        if (props.drawType) {
            setDrawType(props.drawType);
        }
    }, [props.drawType]);

    useEffect(() => {
        if (!map || !props.laancVolumes) return;

        handleResetDrawingData();

        const features = ConvertLAANCVolumesToFeatures(props.laancVolumes);
        const layer = GetLayer(map, "laanc_volumes");
        if (layer) {
            const source = layer.getSource();
            if (source) {
                source.clear();
                source.addFeatures(features);
            }
        } else {
            const vectorLayer = new VectorLayer({
                source: new VectorSource({ features: features }),
                name: "laanc_volumes",
                zIndex: 5,
                properties: { selectable: true }
            });
            map.addLayer(vectorLayer);
        }

        JumpToFeatures(map, features);
    }, [map, props.laancVolumes]);

    useEffect(() => {
        if (!map) return;

        const feature = GetFeature(map, "laanc_volumes", props.selectedLAANCVolume);
        setSelectedLaancFeature(feature);
        JumpToFeature(map, feature);
        if (props.setSelectedLAANCVolume) {
            props.setSelectedLAANCVolume(null);
        }
    }, [map, props.selectedLAANCVolume]);

    useEffect(() => {
        if (!map || !props.clearFeature) return;

        setClearFeature(true);
    }, [map, props.clearFeature]);

    useEffect(() => {
        if (!map) return;

        const layerNames = [
            { name: "flights", volumes: props.publishedFlights },
            { name: "constraints", volumes: props.constraints },
            { name: "alerts", volumes: props.alerts },
            { name: "coa", volumes: props.coa }
        ];
        layerNames.forEach(({ name, volumes }) => {
            if (!name || !volumes) return;
            let features = ConvertCmpVolumesToFeatures(volumes, userMapSettings);

            // if flight layer, check for conflicted flights
            if (name === "flights" && Array.isArray(props.conflictedFlights) && props.conflictedFlights.length > 0) {
                // filter out conflicted features
                features = features.filter((feature) => {
                    const isConflictedFlight = props.conflictedFlights.find((conflictedFlight) => {
                        return feature.getId().includes(conflictedFlight.flight_uuid);
                    });
                    return isConflictedFlight ? false : true;
                });
                // add and style conflicted flights separately
                const conflictedFlightFeatures = ConvertCmpVolumesToFeatures(props.conflictedFlights, userMapSettings);
                conflictedFlightFeatures.forEach((conflictedFlightFeature) => {
                    const stroke = new Stroke({ color: convertHexColorToRgbaColor("#f44336", 1), width: 2 });
                    conflictedFlightFeature.getStyle().setStroke(stroke);

                    const fill = new Fill({ color: convertHexColorToRgbaColor("#f44336", userMapSettings.op_opacity) });
                    conflictedFlightFeature.getStyle().setFill(fill);
                    features.push(conflictedFlightFeature);
                });
            }

            // Ignore conflicted constraints (they get added manually above).
            if (name === "constraints" && Array.isArray(props.conflictedFlights) && props.conflictedFlights.length > 0) {
                // filter out conflicted features
                features = features.filter((feature) => {
                    const isConflictedConstraint = props.conflictedFlights.find((conflict) => {
                        return feature.getId().includes(conflict.constraint_uuid);
                    });
                    return !isConflictedConstraint;
                });
            }

            const layer = GetLayer(map, name);
            if (layer) {
                const source = layer.getSource();
                if (source) {
                    source.clear();
                    source.addFeatures(features);
                }
            } else {
                const volumesLayer = new VectorLayer({
                    source: new VectorSource({ features: features }),
                    properties: { name: name }
                });
                map.addLayer(volumesLayer);
            }
        });

        if (alertOperation) {
            GetAllFeaturesWithId(map, "flights", alertOperation.flight_uuid).forEach((f) => {
                const style = f.getStyle();
                style.getStroke().setColor(convertHexColorToRgbaColor("#FFD700", 1));
                f.setStyle(style);
            });
        }
    }, [map, props.publishedFlights, props.conflictedFlights, props.constraints, props.alerts, props.coa]);

    useEffect(() => {
        if (!map || !props.currentEditVolumes) return;

        drawEditVolumes();
    }, [map, props.currentEditVolumes]);

    useEffect(() => {
        if (!map || !props.radius || !Array.isArray(volumeFeaturesRef.current) || volumeFeaturesRef.current.length !== 1) {
            return;
        }
        const geometry = volumeFeaturesRef.current[0].getGeometry();
        if (!geometry || !(geometry instanceof Polygon)) {
            return;
        }
        const center = geometry.getInteriorPoint().getCoordinates();
        const radiusMeters = ConvertFeetToMeters(props.radius);

        const circle = circular(center, radiusMeters, 128);
        circle.set("type", "Circle");
        volumeFeaturesRef.current[0].setGeometry(circle);

        const geojson = GetCoordinatesForSubmissions(geometry);
        props.setVolumeGeojson(geojson.coordinates);
    }, [map, props.radius]);

    useEffect(() => {
        if (!map) return;

        if (props.canEdit) {
            const layer = GetLayer(map, "laanc_volumes");
            if (layer) map.removeLayer(layer);

            if (!volumeLayerRef.current) {
                handleDrawTypeChange(undefined, "Polygon");
            } else {
                if (props.currentEditVolumes) drawEditVolumes();
            }
        } else {
            handleDrawOff();
        }
    }, [map, props.canEdit]);

    useEffect(() => {
        if (!map || drawType !== "Waypoints") {
            return;
        }
        volumeGeofenceBufferRef.current = props.buffer;

        if (Array.isArray(props.volumeWaypoints) && props.volumeWaypoints.length > 1) {
            const coordsGeojson = props.volumeWaypoints.map(({ lat, lng }) => {
                return [lng, lat];
            });
            // update operational volume
            if (Array.isArray(volumeFeaturesRef.current) && volumeFeaturesRef.current.length === 1) {
                const geofenceCoords = getGeofenceCoordinatesFromCoordinatesTypeAndBuffer(coordsGeojson, props.geofenceType, volumeGeofenceBufferRef.current);
                if (geofenceCoords) {
                    const geofenceGeometry = volumeFeaturesRef.current[0].getGeometry();
                    geofenceGeometry.setCoordinates(geofenceCoords);

                    const geojson = GetCoordinatesForSubmissions(geofenceGeometry);
                    props.setVolumeGeojson(geojson.coordinates);
                }
            }
            // update waypoint feature coordinates
            if (volumeWaypointFeatureRef.current) {
                const waypointGeometry = volumeWaypointFeatureRef.current.getGeometry();
                waypointGeometry.setCoordinates(coordsGeojson);

                const waypointStyle = getWaypointFeatureStyleFromFeatureAndSettings(volumeWaypointFeatureRef.current, userMapSettings);
                volumeWaypointFeatureRef.current.setStyle(waypointStyle);
            }
            // update waypoint reference
            volumeWaypointsRef.current = props.volumeWaypoints.map(({ lng, lat, alt_agl_ft }) => {
                return { lng: lng, lat: lat, alt_agl_ft: alt_agl_ft };
            });
        } else if (volumeLayerRef.current) {
            handleResetDrawingData();
        }
    }, [map, props.volumeWaypoints, props.geofenceType, props.buffer]);

    useEffect(() => {
        if (
            !map ||
            !Array.isArray(volumeFeaturesRef.current) ||
            !Array.isArray(volumeAutomaticFeaturesRef.current) ||
            !props.automaticBuffer ||
            props.automaticBuffer < 0
        ) {
            return;
        }
        const volumeGeojson = [];
        volumeFeaturesRef.current.forEach((feature, i) => {
            const geoJson = ConvertGeometryToGeoJson(feature.getGeometry());
            const buffered = turf.buffer(geoJson, props.automaticBuffer, { units: "feet" });
            const coords = buffered.geometry.coordinates;

            const geometry = volumeAutomaticFeaturesRef.current[i].getGeometry();
            geometry.setCoordinates(coords);

            const featureCoords = GetCoordinatesForSubmissions(geometry);
            volumeGeojson.push(featureCoords.coordinates[0]);
        });
        props.setVolumeGeojson(volumeGeojson);
    }, [map, props.automaticBuffer]);

    useEffect(() => {
        const op = props.automaticOperation;
        const prevOp = alertOperation;
        if (!map || !op) {
            return;
        }

        // Reset prev op + add op highlight.
        if (op !== prevOp) {
            if (prevOp) {
                GetAllFeaturesWithId(map, "flights", prevOp.flight_uuid).forEach((f) => {
                    const style = f.getStyle();
                    const color = GetColorFromState(f.getProperties().flight.state, userMapSettings.op_state_settings);
                    style.getStroke().setColor(convertHexColorToRgbaColor(color, 1));
                    f.setStyle(style);
                });
            }
            GetAllFeaturesWithId(map, "flights", op.flight_uuid).forEach((f) => {
                const style = f.getStyle();
                style.getStroke().setColor(convertHexColorToRgbaColor("#FFD700", 1));
                f.setStyle(style);
            });
            setAlertOperation(op);
        }

        if (drawType !== "Automatic" || !props.automaticBuffer || props.automaticBuffer < 0) {
            return;
        }

        props.setMinAltitude(ConvertMetersToFeet(op.volumes[0].altitude_min_agl_m));
        props.setMaxAltitude(ConvertMetersToFeet(op.volumes[0].altitude_max_agl_m));

        const features = ConvertCmpVolumesToFeatures([op], userMapSettings);
        volumeFeaturesRef.current = features;

        const automaticFeatures = [];
        features.forEach((feature) => {
            const geoJson = ConvertGeometryToGeoJson(feature.getGeometry());
            const buffered = turf.buffer(geoJson, props.automaticBuffer, { units: "feet" });

            const bufferedFeature = new Feature({ geometry: new Polygon(buffered.geometry.coordinates) });
            automaticFeatures.push(bufferedFeature);

            const style = GetGeofenceStyle(userMapSettings);
            bufferedFeature.setStyle(style);
        });
        volumeAutomaticFeaturesRef.current = automaticFeatures;

        const volumeGeojson = [];
        automaticFeatures.forEach((feature) => {
            const featureCoords = GetCoordinatesForSubmissions(feature.getGeometry());
            volumeGeojson.push(featureCoords.coordinates[0]);
        });
        props.setVolumeGeojson(volumeGeojson);

        if (volumeLayerRef.current) {
            map.removeLayer(volumeLayerRef.current);
        }
        volumeLayerRef.current = new VectorLayer({
            source: new VectorSource({ features: automaticFeatures }),
            zIndex: 1
        });
        map.addLayer(volumeLayerRef.current);
        JumpToFeatures(map, automaticFeatures);
    }, [props.automaticOperation, drawType]);

    useEffect(() => {
        if (!props.volumeGeojson || !volumeLayerRef.current) {
            return;
        }

        const color = colors.find((color) => color.id === props.colorId);
        const colorHex = color ? color.color_rgb : null;
        const style = GetEditVolumeStyle(userMapSettings, colorHex);
        volumeLayerRef.current.getSource().forEachFeature((f) => f.setStyle(style));
    }, [props.volumeGeojson, props.colorId]);

    useEffect(() => {
        if (!drawType) {
            return;
        }

        // If a volume isn't fully draw yet, we will reset it with the new color.
        if (!props.volumeGeojson || !volumeLayerRef.current) {
            handleDrawTypeChange(null, drawType);
        }
    }, [props.colorId, colors]);

    useEffect(() => {
        if (!map || !props.getScreenshot) return;

        const filter = (node) => {
            const exclusionClasses = ["MuiSvgIcon-root"];
            return !exclusionClasses.some((classname) => node.classList?.contains(classname));
        };

        map.once("rendercomplete", function () {
            toJpeg(map.getTargetElement(), { quality: 0.95, filter: filter, skipFonts: true }).then(function (dataURL) {
                props.setPrintMap(dataURL);
            });
        });
    }, [map, props.getScreenshot]);

    const drawEditVolumes = () => {
        handleResetDrawingData();
        const features = ConvertCmpVolumesToFeatures([props.currentEditVolumes], userMapSettings);
        handleDrawFeatures(features);
    };

    const handleToggleLayersButtonClick = (e) => {
        setMapLayersAnchorEl(e.currentTarget);
        setMapLayersOpen((prev) => !prev);
    };
    const handleDrawTypeChange = (e, value) => {
        if (!map) {
            return;
        } else if (!value) {
            if (drawType === "Upload") {
                volumeUploadRef.current.click();
            }
            return;
        }

        handleResetDrawingData();
        setDrawType(value);
        props.changeDrawType(value);

        if (value === "Upload") {
            volumeUploadRef.current.click();
            return;
        } else if (value === "Clear") {
            return;
        }

        if (value === "Automatic") {
            return;
        }

        drawRef.current = new Draw({
            source: new VectorSource({ wrapX: false }),
            type: value === "Waypoints" ? "LineString" : value === "Circle" ? "Point" : value,
            style: GetEditVolumeStyle(userMapSettings, colors.find((color) => color.id === props.colorId)?.color_rgb)
        });
        drawRef.current.on("drawend", handleDrawEnd);
        map.addInteraction(drawRef.current);
    };

    const handleDrawEnd = ({ feature }) => {
        if (drawRef.current) {
            map.removeInteraction(drawRef.current);
            drawRef.current = null;
        }

        const editableFeatures = new Collection([feature]);
        const layerFeatures = new Collection([feature]);

        // extract data from the drawn feature
        let geometry = feature.getGeometry();
        if (geometry instanceof LineString) {
            volumeWaypointFeatureRef.current = feature;

            const waypointStyle = getWaypointFeatureStyleFromFeatureAndSettings(feature, userMapSettings);
            volumeWaypointFeatureRef.current.setStyle(waypointStyle);

            const waypointCoords = geometry.getCoordinates();
            volumeWaypointsRef.current = waypointCoords.map(([lng, lat]) => {
                return { lng: lng, lat: lat, alt_agl_ft: defaultWaypointAltitudeRef.current };
            });

            const geofenceFeature = getGeofenceFeatureFromGeometryAndBuffer(geometry, props.buffer, userMapSettings);
            layerFeatures.push(geofenceFeature);

            volumeFeaturesRef.current = [geofenceFeature];
            geometry = geofenceFeature.getGeometry();
        } else {
            const style = GetEditVolumeStyle(userMapSettings);
            feature.setStyle(style);
            volumeFeaturesRef.current = [feature];
        }
        if (geometry instanceof Point) {
            geometry = circular(geometry.getCoordinates(), ConvertFeetToMeters(1000), 128);
            geometry.set("type", "Circle");
            feature.set("type", "Circle");
            feature.setGeometry(geometry);
            props.setRadius(1000);
        }
        JumpToFeature(map, feature);
        if (props.setMobileDrawerOpen) {
            props.setMobileDrawerOpen(true);
        }

        const geojson = GetCoordinatesForSubmissions(geometry);
        props.setVolumeGeojson(geojson.coordinates);
        if (props.setVolumeWaypoints) {
            props.setVolumeWaypoints(volumeWaypointsRef.current);
        }

        // add the feature to the map
        volumeLayerRef.current = new VectorLayer({
            source: new VectorSource({ features: layerFeatures }),
            zIndex: 1
        });
        map.addLayer(volumeLayerRef.current);

        // add the ability to move the feature
        translateRef.current = new Translate({
            filter: (f) => f === feature
        });
        map.addInteraction(translateRef.current);
        translateRef.current.on("translateend", handleGeometryUpdate);

        // add the ability to edit or add vertices
        if (feature.get("type") !== "Circle") {
            modifyRef.current = new Modify({
                features: editableFeatures,
                deleteCondition: (event) => event.originalEvent.button === 2
            });
            map.addInteraction(modifyRef.current);
            modifyRef.current.on("modifyend", handleGeometryUpdate);
        }

        // add vertex snapping
        snapRef.current = new Snap({
            features: editableFeatures
        });
        map.addInteraction(snapRef.current);
    };

    const handleGeometryUpdate = ({ features }) => {
        if (features.getLength() < 1) {
            return;
        }
        const feature = features.item(0); // this may need to be updated??

        let geometry = feature.getGeometry();
        if (geometry instanceof LineString) {
            // update the waypoint feature style
            feature.setStyle(getWaypointFeatureStyleFromFeatureAndSettings(feature, userMapSettings));
            volumeWaypointFeatureRef.current = feature;

            // update the geofence feature
            const waypointCoords = geometry.getCoordinates();
            if (Array.isArray(volumeFeaturesRef.current) && volumeFeaturesRef.current[0] && volumeGeofenceBufferRef.current) {
                const geofenceCoords = getGeofenceCoordinatesFromCoordinatesTypeAndBuffer(waypointCoords, props.geofenceType, volumeGeofenceBufferRef.current);
                if (geofenceCoords) {
                    const geofenceGeometry = volumeFeaturesRef.current[0].getGeometry();
                    geofenceGeometry.setCoordinates(geofenceCoords);
                    geometry = geofenceGeometry;
                }
            }
            // update the waypoint reference for submission
            const flightWaypoints = getUpdatedWaypointsFromCoordinatesAndDefaultAltitude(
                waypointCoords,
                volumeWaypointsRef.current,
                defaultWaypointAltitudeRef.current
            );
            volumeWaypointsRef.current = flightWaypoints;

            if (props.setVolumeWaypoints) {
                props.setVolumeWaypoints(flightWaypoints);
            }
        } else {
            volumeFeaturesRef.current = [feature];
        }
        const geojson = GetCoordinatesForSubmissions(geometry);
        props.setVolumeGeojson(geojson.coordinates);
    };

    const handleGeometriesUpdate = () => {
        const layer = volumeLayerRef.current;
        const source = layer.getSource();
        const features = source.getFeatures();

        const coords = [];
        features.forEach((feature) => {
            coords.push(GetCoordinatesForSubmissions(feature.getGeometry()).coordinates[0]);
        });

        props.setVolumeGeojson(coords);
        if (props.setVolumeWaypoints) {
            props.setVolumeWaypoints([]);
        }
    };

    const handleResetDrawingData = () => {
        if (volumeLayerRef.current) {
            map.removeLayer(volumeLayerRef.current);
            volumeLayerRef.current = null;
        }
        handleDrawOff();
        volumeFeaturesRef.current = null;
        volumeWaypointFeatureRef.current = null;
        volumeWaypointsRef.current = [];

        props.setVolumeGeojson([]);
        if (props.setVolumeWaypoints) {
            props.setVolumeWaypoints([]);
        }
    };

    const handleDrawOff = () => {
        if (drawRef.current) {
            map.removeInteraction(drawRef.current);
            drawRef.current = null;
        }
        if (modifyRef.current) {
            map.removeInteraction(modifyRef.current);
            modifyRef.current = null;
        }
        if (snapRef.current) {
            map.removeInteraction(snapRef.current);
            snapRef.current = null;
        }
        if (translateRef.current) {
            map.removeInteraction(translateRef.current);
            translateRef.current = null;
        }
    };

    const handleDrawFeatures = (features) => {
        const coords = [];
        features.forEach((feature) => {
            feature.set("edit", true);
            feature.setStyle(GetEditVolumeStyle(userMapSettings));
            const featureCoords = GetCoordinatesForSubmissions(feature.getGeometry());
            coords.push(featureCoords.coordinates[0]);
        });

        props.setVolumeGeojson(coords);

        // add the feature to the map
        const source = new VectorSource({ features: features });
        volumeLayerRef.current = new VectorLayer({
            source: source,
            zIndex: 1
        });
        map.addLayer(volumeLayerRef.current);
        JumpToFeatures(map, features);

        if (props.drawType === "Circle") {
            volumeFeaturesRef.current = [features[0]];
        } else {
            modifyRef.current = new Modify({ source: source });
            map.addInteraction(modifyRef.current);
            modifyRef.current.on("modifyend", handleGeometriesUpdate);
        }
    };

    const handleUpload = (files) => {
        handleResetDrawingData();
        ConvertKmlFileToFeatures(files[0])
            .then((features) => {
                props.setName(features[0].get("name"));
                props.setMinAltitude(ConvertMetersToFeet(features[0].get("altitudes").minAltitude));
                props.setMaxAltitude(ConvertMetersToFeet(features[0].get("altitudes").maxAltitude));
                handleDrawFeatures(features);
            })
            .catch((error) => {
                setSnackbar({ children: error, severity: "error" });
            });
    };

    const handleMapTileDropdownButtonClick = (e) => {
        setMapTileDropdownOpen((prev) => !prev);
        setMapTileDropdownAnchorEl(e.currentTarget);
    };

    const handleHomeButtonClick = () => {
        if (map && map.getView()) {
            map.getView().setCenter([userMapSettings.longitude, userMapSettings.latitude]);
            map.getView().setZoom(12);
        }
    };

    return (
        <Box style={{ position: "relative", width: "100%", height: "100%", display: "flex" }}>
            <MapFullScreen
                map_id={MAP_ID}
                map={map}
                buttons={[
                    {
                        name: "Toggle Layers",
                        visible: true,
                        icon: <LayersIcon />,
                        onClick: handleToggleLayersButtonClick
                    },
                    {
                        id: "toggleMapTiles",
                        name: "Map Tiles",
                        visible: true,
                        icon: <MapIcon />,
                        onClick: handleMapTileDropdownButtonClick
                    },
                    {
                        id: "viewHome",
                        name: "View Home",
                        visible: true,
                        icon: <HomeIcon />,
                        onClick: handleHomeButtonClick
                    }
                ]}
            >
                <MapInfoCard
                    map={map}
                    showButtons={false}
                    featureToShow={selectedLaancFeature}
                    clearFeature={clearFeature}
                    airspaceSubLayers={airspaceSubLayers}
                />
            </MapFullScreen>

            <Typography
                ref={mapLocationRef}
                variant="caption"
                sx={{
                    position: "absolute",
                    right: "0.5em",
                    bottom: ".5em",
                    zIndex: 1,
                    fontFamily: "Arial,sans-serif",
                    textShadow:
                        "-1px -1px 0 black, 1px -1px 0 black, -1px 1px 0 black, 1px 1px 0 black, -1px 0 0 black, 1px 0 0 black, 0 1px 0 black, 0 -1px 0 black, 0 0 5px black"
                }}
            />
            <input
                type="file"
                ref={volumeUploadRef}
                style={{ display: "none" }}
                onChange={(e) => handleUpload(e.target.files)}
                onClick={(e) => (e.target.value = "")}
                id="fileUpload"
            />
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: 1,
                    position: "absolute",
                    left: "15px",
                    top: "15px",
                    zIndex: 100,
                    background: "#121212"
                }}
            >
                {props.canEdit ? (
                    <ToggleButtonGroup orientation="vertical" color="primary" value={drawType} onChange={handleDrawTypeChange} exclusive sx={{ width: "48px" }}>
                        {volumeType?.drawTypes.map((drawType, i) => (
                            <Tooltip key={i} title={drawType.name} placement="right">
                                <ToggleButton value={drawType.name} id={drawType.name.toLowerCase()}>
                                    {drawType.icon}
                                </ToggleButton>
                            </Tooltip>
                        ))}
                    </ToggleButtonGroup>
                ) : (
                    <></>
                )}
            </Box>

            <MapLaancLayersDropdown
                map={map}
                mapLayersOpen={mapLayersOpen}
                setMapLayersOpen={setMapLayersOpen}
                mapLayersAnchorEl={mapLayersAnchorEl}
                setMapLayersAnchorEl={setMapLayersAnchorEl}
                layers={volumeType?.layers}
                airspaceSubLayers={airspaceSubLayers}
                setAirspaceSublayers={setAirspaceSublayers}
                timerange={props.timerange}
            />
            <MapTileDropdown
                mapTileDropdownOpen={mapTileDropdownOpen}
                setMapTileDropdownOpen={setMapTileDropdownOpen}
                mapTileDropdownAnchorEl={mapTileDropdownAnchorEl}
            />
        </Box>
    );
}
